import { shikiToMonaco } from "@shikijs/monaco";
import type * as Monaco from "monaco-editor";
import type { Highlighter } from "shiki";

/**
 * The code area is `--dark-1`, not white: on a white card the editor had no edge of its own,
 * so the gutter, the minimap and the text all floated in the same sheet. Ported from
 * KOA-13392's `kuika-sql-light`.
 *
 * Monaco cannot read CSS variables, so the theme carries literals and is re-declared on every
 * theme flip — one name, redefined, rather than two the host would have to choose between.
 */
export const CODE_EDITOR_THEME: string = "kuika-code";

type TAppTheme = "light" | "dark";

interface ICodeEditorSurface {
  /** `vs` / `vs-dark` carry the syntax palette, minus the SQL rules below; the chrome follows the tokens. */
  base: "vs" | "vs-dark";
  /** `--dark-1`, read live where possible so a token change reaches the editor too. */
  surfaceFallback: string;
  slider: string;
  sliderHover: string;
  sliderActive: string;
}

const SURFACE_TOKEN: string = "--dark-1";

/**
 * `vs` / `vs-dark` give SQL two colours nobody would ship: `predefined.sql` is full magenta
 * (`#FF00FF`) and `string.sql` full red (`#FF0000`), so `UPDATE`, `@@ROWCOUNT` and every literal
 * glare out of an otherwise muted editor. These pull them back onto the same palette the rest of
 * the syntax already uses — Monaco's own `keyword.flow` purple and its `string` colour.
 */
const SQL_TOKEN_RULES: Record<TAppTheme, Monaco.editor.ITokenThemeRule[]> = {
  light: [
    { token: "predefined.sql", foreground: "AF00DB" },
    { token: "string.sql", foreground: "A31515" }
  ],
  dark: [
    { token: "predefined.sql", foreground: "C586C0" },
    { token: "string.sql", foreground: "CE9178" }
  ]
};

/** The syntax rules a Monaco theme built on `vs` / `vs-dark` needs, for the given appearance. */
export const codeEditorTokenRules = (theme: TAppTheme): Monaco.editor.ITokenThemeRule[] => SQL_TOKEN_RULES[theme];

/** Shiki's tokens provider and its `setTheme` patch are global; both belong to the session. */
let installedHighlighter: Highlighter | null = null;
/** Monaco's own `setTheme`, kept from before Shiki replaced it. */
let setMonacoTheme: ((themeName: string) => void) | null = null;

const SURFACES: Record<TAppTheme, ICodeEditorSurface> = {
  light: {
    base: "vs",
    surfaceFallback: "#F7F8F9",
    // Monaco's default dark scrollbar looks wrong on the card edge.
    slider: "#DCDFE499",
    sliderHover: "#C7CBD3CC",
    sliderActive: "#B4B9C3"
  },
  dark: {
    base: "vs-dark",
    surfaceFallback: "#31343A",
    slider: "#4F566199",
    sliderHover: "#6A7381CC",
    sliderActive: "#8490A1"
  }
};

/** Light or dark, as the app currently has it. */
export const resolveAppTheme = (): TAppTheme =>
  document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";

const readSurface = (surface: ICodeEditorSurface): string => {
  const token: string = getComputedStyle(document.documentElement).getPropertyValue(SURFACE_TOKEN).trim();
  return token || surface.surfaceFallback;
};

/** Declares the theme for the app's current appearance and applies it. */
export const defineCodeEditorTheme = (monacoInstance: typeof Monaco): void => {
  const theme: TAppTheme = resolveAppTheme();
  const surface: ICodeEditorSurface = SURFACES[theme];
  const background: string = readSurface(surface);

  monacoInstance.editor.defineTheme(CODE_EDITOR_THEME, {
    base: surface.base,
    inherit: true,
    rules: codeEditorTokenRules(theme),
    colors: {
      "editor.background": background,
      "editorGutter.background": background,
      "minimap.background": background,
      "scrollbarSlider.background": surface.slider,
      "scrollbarSlider.hoverBackground": surface.sliderHover,
      "scrollbarSlider.activeBackground": surface.sliderActive,
      "editorOverviewRuler.border": "#00000000"
    }
  });
  monacoInstance.editor.setTheme(CODE_EDITOR_THEME);
};

/**
 * Hands Monaco's tokenizer to Shiki for a language Monaco cannot highlight on its own, and keeps
 * every other editor's theme working afterwards.
 *
 * `shikiToMonaco` replaces `monaco.editor.setTheme` with a wrapper that asks the highlighter for
 * the theme first and THROWS on a name it has not loaded. `kuika-code` is a plain Monaco theme,
 * so once the C# action had been opened, mounting any sibling editor threw inside `beforeMount`
 * and took the page down with it. Wrapping once more sends a Shiki theme through Shiki and a
 * Monaco theme straight to Monaco.
 *
 * Monaco is a singleton and Shiki's tokens provider is global, so this installs once per
 * highlighter — a second one only ever arrives on a hot reload, and skipping it there would leave
 * Monaco tokenizing against a highlighter nobody sets a theme on any more.
 */
export const installShikiThemes = (monacoInstance: typeof Monaco, highlighter: Highlighter): void => {
  if (installedHighlighter === highlighter) return;
  if (!setMonacoTheme) setMonacoTheme = monacoInstance.editor.setTheme.bind(monacoInstance.editor);
  installedHighlighter = highlighter;

  const setNativeTheme: (themeName: string) => void = setMonacoTheme;
  shikiToMonaco(highlighter, monacoInstance);
  const setShikiTheme: (themeName: string) => void = monacoInstance.editor.setTheme;

  monacoInstance.editor.setTheme = (themeName: string): void => {
    const isShikiTheme: boolean = highlighter.getLoadedThemes().includes(themeName);
    if (isShikiTheme) setShikiTheme(themeName);
    else setNativeTheme(themeName);
  };
};

/**
 * Flipping the appearance re-renders nothing Monaco owns, so the editor would keep the theme
 * it mounted with. The caller disconnects the returned observer on unmount.
 */
export const watchCodeEditorTheme = (monacoInstance: typeof Monaco): MutationObserver => {
  const observer: MutationObserver = new MutationObserver((): void => defineCodeEditorTheme(monacoInstance));
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return observer;
};

/**
 * For an editor that brings its own themes rather than this one — the C# action highlights
 * through Shiki, which registers its own. The caller gets the appearance on every flip and
 * disconnects the observer on unmount.
 */
export const watchAppTheme = (onChange: (theme: TAppTheme) => void): MutationObserver => {
  const observer: MutationObserver = new MutationObserver((): void => onChange(resolveAppTheme()));
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return observer;
};

export type { TAppTheme };
